import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../hooks/useTheme";

const SUBMIT_URL = "https://readdy.ai/api/form/d70jc4n9hgr6oot2qvtg";
const FORMSPREE_URL = "https://formspree.io/f/mojpoooo";

const INDUSTRIES = [
	"plan_form_ind_restaurant",
	"plan_form_ind_supermarket",
	"plan_form_ind_hardware",
	"plan_form_ind_minimarket",
	"plan_form_ind_distributor",
	"plan_form_ind_loan",
	"plan_form_ind_events",
	"plan_form_ind_tech",
	"plan_form_ind_other",
];

interface PlanFormModalProps {
	planName: string | null;
	planColor: string;
	onClose: () => void;
}

function ModalContent({ planName, planColor, onClose }: PlanFormModalProps) {
	const { t } = useTranslation();
	const { theme } = useTheme();
	const isLight = theme === "light";
	const [status, setStatus] = useState<
		"idle" | "sending" | "success" | "error"
	>("idle");
	const [charCount, setCharCount] = useState(0);

	/* Lock body scroll */
	useEffect(() => {
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, []);

	/* Close on Escape */
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setStatus("sending");
		const data = new URLSearchParams();
		new FormData(e.currentTarget).forEach((val, key) =>
			data.append(key, val.toString()),
		);

		try {
			const [res] = await Promise.allSettled([
				fetch(SUBMIT_URL, {
					method: "POST",
					headers: { "Content-Type": "application/x-www-form-urlencoded" },
					body: data.toString(),
				}),
				fetch(FORMSPREE_URL, {
					method: "POST",
					headers: {
						Accept: "application/json",
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: data.toString(),
				}),
			]);
			setStatus(
				res.status === "fulfilled" && res.value.ok ? "success" : "error",
			);
		} catch {
			setStatus("error");
		}
	};

	const modalBg = isLight ? "#ffffff" : "#0a1628";
	const inputBg = isLight ? "#f5f8ff" : "rgba(255,255,255,0.05)";
	const inputBorder = isLight
		? "rgba(10,22,40,0.15)"
		: "rgba(255,255,255,0.12)";
	const labelColor = isLight ? "rgba(10,22,40,0.65)" : "rgba(255,255,255,0.55)";
	const textColor = isLight ? "#0a1628" : "#ffffff";

	const inputClass =
		"w-full rounded-xl px-4 py-2.5 font-body text-sm outline-none border transition-colors";

	return (
		/*
		 * Overlay: fixed + inset-0 MUST sit on document.body via portal
		 * so overflow-x:hidden on <main> never clips it.
		 * overflow-y:auto on this div lets the overlay scroll on short screens.
		 */
		<div
			className="fixed inset-0 z-[9999] overflow-y-auto"
			style={{
				backgroundColor: "rgba(0,0,0,0.70)",
				backdropFilter: "blur(5px)",
			}}
		>
			{/* Centering wrapper — clicking it (outside card) closes modal */}
			<div
				className="flex min-h-full items-start sm:items-center justify-center p-4 sm:py-10"
				onClick={onClose}
			>
				{/* Card — stopPropagation so inner clicks don't close */}
				<div
					onClick={(e) => e.stopPropagation()}
					className="relative w-full rounded-2xl border flex flex-col"
					style={{
						maxWidth: "32rem",
						background: modalBg,
						borderColor: planColor + "40",
						boxShadow: `0 0 60px ${planColor}20, 0 24px 64px rgba(0,0,0,0.45)`,
					}}
				>
					{/* Accent line */}
					<div
						className="h-1 w-full rounded-t-2xl flex-shrink-0"
						style={{
							background: `linear-gradient(to right, ${planColor}, ${planColor}60, transparent)`,
						}}
					/>

					<div className="p-5 sm:p-7">
						{/* ── Header ── */}
						<div className="flex items-start justify-between mb-5">
							<div>
								<span
									className="inline-block text-xs font-semibold tracking-widest uppercase font-body mb-1"
									style={{ color: planColor }}
								>
									{t("plan_form_selected")}: {planName}
								</span>
								<h3
									className="font-heading text-xl sm:text-2xl font-bold"
									style={{ color: textColor }}
								>
									{t("plan_form_title")}
								</h3>
								<p
									className="font-body text-xs sm:text-sm mt-1"
									style={{ color: labelColor }}
								>
									{t("plan_form_sub")}
								</p>
							</div>
							<button
								onClick={onClose}
								className="ml-4 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full border transition-colors cursor-pointer hover:opacity-80"
								style={{
									borderColor: isLight
										? "rgba(10,22,40,0.15)"
										: "rgba(255,255,255,0.15)",
									color: isLight
										? "rgba(10,22,40,0.50)"
										: "rgba(255,255,255,0.50)",
								}}
							>
								<i className="ri-close-line text-base" />
							</button>
						</div>

						{/* ── Success state ── */}
						{status === "success" ? (
							<div className="text-center py-8">
								<div
									className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4"
									style={{
										backgroundColor: planColor + "18",
										color: planColor,
									}}
								>
									<i className="ri-checkbox-circle-line text-3xl" />
								</div>
								<h4
									className="font-heading text-xl font-bold mb-2"
									style={{ color: textColor }}
								>
									{t("plan_form_success")}
								</h4>
								<p className="font-body text-sm" style={{ color: labelColor }}>
									{t("plan_form_success_sub")}
								</p>
								<button
									onClick={onClose}
									className="mt-6 inline-flex items-center gap-2 font-bold text-sm py-3 px-6 rounded-xl font-body transition-all cursor-pointer whitespace-nowrap hover:scale-105"
									style={{
										background: `linear-gradient(to right, ${planColor}, ${planColor}cc)`,
										color: "#060f23",
									}}
								>
									{t("plan_form_close")}
								</button>
							</div>
						) : (
							/* ── Form ── */
							<form
								onSubmit={handleSubmit}
								data-readdy-form
								id="plan-selection-form"
								className="space-y-3 sm:space-y-4"
							>
								<input type="hidden" name="plan" value={planName ?? ""} />

								{/* Name */}
								<div>
									<label
										className="block font-body text-xs font-semibold mb-1.5"
										style={{ color: labelColor }}
									>
										{t("plan_form_name")} *
									</label>
									<input
										type="text"
										name="name"
										required
										placeholder={t("plan_form_name_placeholder")}
										className={inputClass}
										style={{
											background: inputBg,
											borderColor: inputBorder,
											color: textColor,
										}}
										onFocus={(e) => {
											e.currentTarget.style.borderColor = planColor;
										}}
										onBlur={(e) => {
											e.currentTarget.style.borderColor = inputBorder;
										}}
									/>
								</div>

								{/* Phone + Email */}
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
									<div>
										<label
											className="block font-body text-xs font-semibold mb-1.5"
											style={{ color: labelColor }}
										>
											{t("plan_form_phone")} *
										</label>
										<input
											type="tel"
											name="phone"
											required
											placeholder={t("plan_form_phone_placeholder")}
											className={inputClass}
											style={{
												background: inputBg,
												borderColor: inputBorder,
												color: textColor,
											}}
											onFocus={(e) => {
												e.currentTarget.style.borderColor = planColor;
											}}
											onBlur={(e) => {
												e.currentTarget.style.borderColor = inputBorder;
											}}
										/>
									</div>
									<div>
										<label
											className="block font-body text-xs font-semibold mb-1.5"
											style={{ color: labelColor }}
										>
											{t("plan_form_email")} *
										</label>
										<input
											type="email"
											name="email"
											required
											placeholder={t("plan_form_email_placeholder")}
											className={inputClass}
											style={{
												background: inputBg,
												borderColor: inputBorder,
												color: textColor,
											}}
											onFocus={(e) => {
												e.currentTarget.style.borderColor = planColor;
											}}
											onBlur={(e) => {
												e.currentTarget.style.borderColor = inputBorder;
											}}
										/>
									</div>
								</div>

								{/* Business name */}
								<div>
									<label
										className="block font-body text-xs font-semibold mb-1.5"
										style={{ color: labelColor }}
									>
										{t("plan_form_business")} *
									</label>
									<input
										type="text"
										name="business_name"
										required
										placeholder={t("plan_form_business_placeholder")}
										className={inputClass}
										style={{
											background: inputBg,
											borderColor: inputBorder,
											color: textColor,
										}}
										onFocus={(e) => {
											e.currentTarget.style.borderColor = planColor;
										}}
										onBlur={(e) => {
											e.currentTarget.style.borderColor = inputBorder;
										}}
									/>
								</div>

								{/* Industry */}
								<div>
									<label
										className="block font-body text-xs font-semibold mb-1.5"
										style={{ color: labelColor }}
									>
										{t("plan_form_type")}
									</label>
									<select
										name="business_type"
										className="w-full rounded-xl px-4 py-2.5 font-body text-sm outline-none border transition-colors appearance-none cursor-pointer"
										style={{
											background: inputBg,
											borderColor: inputBorder,
											color: textColor,
										}}
										onFocus={(e) => {
											e.currentTarget.style.borderColor = planColor;
										}}
										onBlur={(e) => {
											e.currentTarget.style.borderColor = inputBorder;
										}}
									>
										<option value="">{t("contact_select_placeholder")}</option>
										{INDUSTRIES.map((key) => (
											<option key={key} value={t(key)}>
												{t(key)}
											</option>
										))}
									</select>
								</div>

								{/* Implementation type */}
								<div>
									<label
										className="block font-body text-xs font-semibold mb-1.5"
										style={{ color: labelColor }}
									>
										{t("plan_form_implementation")}
									</label>
									<select
										name="implementation_type"
										className="w-full rounded-xl px-4 py-2.5 font-body text-sm outline-none border transition-colors appearance-none cursor-pointer"
										style={{
											background: inputBg,
											borderColor: inputBorder,
											color: textColor,
										}}
										onFocus={(e) => {
											e.currentTarget.style.borderColor = planColor;
										}}
										onBlur={(e) => {
											e.currentTarget.style.borderColor = inputBorder;
										}}
									>
										<option value="">{t("contact_req_placeholder")}</option>
										<option value="Implementación Estándar - $200">
											{t("plan_form_impl_standard")}
										</option>
										<option value="Paquete Completo con e-CF - $450">
											{t("plan_form_impl_full_bundle")}
										</option>
										<option value="Solo Certificación e-CF - $300">
											{t("plan_form_impl_fel_only")}
										</option>
									</select>
								</div>

								{/* Message */}
								<div>
									<label
										className="flex items-center justify-between font-body text-xs font-semibold mb-1.5"
										style={{ color: labelColor }}
									>
										<span>{t("plan_form_msg")}</span>
										<span
											style={{
												color: charCount > 450 ? "#ef4444" : labelColor,
											}}
										>
											{charCount}/500
										</span>
									</label>
									<textarea
										name="message"
										rows={3}
										maxLength={500}
										placeholder={t("plan_form_msg_placeholder")}
										onChange={(e) => setCharCount(e.target.value.length)}
										className="w-full rounded-xl px-4 py-2.5 font-body text-sm outline-none border transition-colors resize-none"
										style={{
											background: inputBg,
											borderColor: inputBorder,
											color: textColor,
										}}
										onFocus={(e) => {
											e.currentTarget.style.borderColor = planColor;
										}}
										onBlur={(e) => {
											e.currentTarget.style.borderColor = inputBorder;
										}}
									/>
								</div>

								{status === "error" && (
									<p className="text-red-400 text-xs font-body">
										{t("plan_form_error")}
									</p>
								)}

								{/* Submit */}
								<button
									type="submit"
									disabled={status === "sending"}
									className="w-full flex items-center justify-center gap-2 font-bold text-sm py-3.5 rounded-xl font-body transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
									style={{
										background: `linear-gradient(to right, ${planColor}, ${planColor}cc)`,
										color: "#060f23",
									}}
								>
									{status === "sending" ? (
										<>
											<i className="ri-loader-4-line animate-spin text-sm" />
											{t("plan_form_sending")}
										</>
									) : (
										<>
											<i className="ri-send-plane-2-line text-sm" />
											{t("plan_form_send")}
										</>
									)}
								</button>
							</form>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default function PlanFormModal(props: PlanFormModalProps) {
	return createPortal(<ModalContent {...props} />, document.body);
}
